import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from 'auth/AuthProvider';
import { invalidateAfterCompanyPurchase } from 'core/api/invalidation';
import { queryKeys } from 'core/api/queryKeys';
import { ApiError } from 'core/errors/ApiError';
import { humanizeApiError } from 'core/errors/mapFieldErrors';
import { useI18n } from 'core/i18n/I18nProvider';
import { paths } from 'core/routing/paths';
import { companyApi } from 'features/company/company.api';
import { createIdempotencyKey, formatMoney } from 'features/company/companyUtils';
import { Card, ErrorState, HeroBand, Skeleton } from 'shared/components/Feedback';
import { PageMeta } from 'shared/components/PageMeta';
import { localizedText, safeHttpUrl } from 'shared/utilities/format';

export function CompanyPlanDetailPage() {
  const { planId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { status, user } = useAuth();
  const { t, locale } = useI18n();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const query = useQuery({
    queryKey: queryKeys.companyPlan(planId),
    queryFn: () => companyApi.planFromCatalog(planId),
  });

  const handlePurchase = async () => {
    if (status !== 'authenticated') {
      navigate(`${paths.auth}?returnTo=${encodeURIComponent(paths.companyPlan(planId))}`);
      return;
    }
    if (submitting) {
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const session = await companyApi.resolveSession(user.id);
      const idempotencyKey = createIdempotencyKey('purchase');
      const response = await companyApi.startPurchase(session.company.id, planId, idempotencyKey);
      const purchase = response.purchase || response;
      await invalidateAfterCompanyPurchase(queryClient, purchase.id);
      const checkoutUrl = safeHttpUrl(purchase.checkout_url || response.payment?.checkout_url);
      if (checkoutUrl) {
        window.location.assign(checkoutUrl);
        return;
      }
      navigate(paths.companyPurchaseStatus(purchase.id));
    } catch (err) {
      setError(humanizeApiError(err, t));
      if (err instanceof ApiError && (err.isForbidden() || err.status === 404)) {
        navigate(paths.companyOnboarding);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (query.isLoading) {
    return <Skeleton rows={8} />;
  }
  if (query.isError) {
    return <ErrorState title={humanizeApiError(query.error, t)} onRetry={() => query.refetch()} retryLabel={t('actions.retry')} />;
  }

  const plan = query.data?.plan || query.data || {};
  return (
    <div className="company-plan-detail">
      <PageMeta titleKey="meta.companyPlan" />
      <HeroBand
        kicker={t('company.plans.kicker')}
        title={localizedText(plan, locale, ['name'])}
        body={localizedText(plan, locale, ['description'])}
      />
      <Card>
        <h2>{t('company.plans.detailsTitle')}</h2>
        <ul className="plan-meta">
          <li>{t('company.plans.credits', { count: plan.attempt_credits })}</li>
          <li>{formatMoney(plan.price_minor, plan.currency, locale)}</li>
          {plan.validity_days ? <li>{t('company.plans.validity', { days: plan.validity_days })}</li> : null}
        </ul>
        <p className="muted">{t('company.plans.companyExplain')}</p>
        {localizedText(plan, locale, ['terms']) ? (
          <div className="legal-copy">
            <h3>{t('company.plans.terms')}</h3>
            <p>{localizedText(plan, locale, ['terms'])}</p>
          </div>
        ) : null}
        {error ? <p className="field-error" role="alert">{error}</p> : null}
        <div className="action-row">
          <button type="button" className="btn btn-primary" disabled={submitting} onClick={() => { void handlePurchase(); }}>
            {submitting ? t('company.purchase.processing') : t('company.plans.purchase')}
          </button>
          <Link className="btn btn-secondary" to={paths.companyPlans}>{t('actions.back')}</Link>
        </div>
      </Card>
    </div>
  );
}
